// Device templates: the register map of a model, saved once and reused.
//
// Two jobs, deliberately in one place because they are two ends of one loop:
// browsing what has been saved (and cloning or removing it), and using one to
// start a connection. "Use" does not connect anything — it derives the spec on
// the server, fills the ordinary Modbus wizard with it, and hands the operator
// back to the same preview/confirm review every other connection goes through.
// A template is a faster way to fill the form, never a way around the review.

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  cloneDeviceTemplate,
  createDeviceTemplate,
  deleteDeviceTemplate,
  instantiateDeviceTemplate,
  listDeviceTemplates,
} from "../../../api";
import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import xmarkIcon from "../../../assets/xmark.svg";
import { edgeModbusSpecKind, edgeModbusSpecToDraft } from "./edge-connector-code";

const KIND_LABELS = { "modbus-tcp": "Modbus TCP", "modbus-rtu": "Modbus RTU" };

const POINT_FUNCTION_LABELS = { 1: "Coil", 2: "Discrete input", 3: "Holding register", 4: "Input register" };

export const templateSummaryLine = (template) => {
  const points = Array.isArray(template?.points) ? template.points : [];
  const writable = points.filter((point) => point.writable).length;
  const parts = [
    KIND_LABELS[template?.kind] || template?.kind || "Modbus",
    `${points.length} ${points.length === 1 ? "point" : "points"}`,
  ];
  if (writable) parts.push(`${writable} writable`);
  if (template?.tag_folder_default) parts.push(`tags under default/${template.tag_folder_default}/`);
  return parts.join(" · ");
};

export const templatePointLine = (point) => {
  const label = POINT_FUNCTION_LABELS[Number(point?.function)] || `Function ${point?.function}`;
  const bit = point?.bit == null ? "" : ` · bit ${point.bit}`;
  const scale = point?.scale != null && Number(point.scale) !== 1 ? ` · ×${point.scale}` : "";
  return `${point?.name} — ${label} ${point?.address}${bit} · ${point?.value_type || "bool"}${scale}${point?.writable ? " · read and write" : ""}`;
};

const publicTemplateError = (error, fallback) => {
  const message = String(error?.publicMessage || error?.message || "").trim();
  if (!message) return fallback;
  // The Go handler passes its validation refusals through verbatim: they name
  // the offending point and the rule it broke, which is what makes them
  // fixable. Anything else stays behind the fallback sentence.
  return /devicetemplates: /.test(message) ? message.replace(/^.*devicetemplates: /, "") : fallback;
};

function TemplatePoints({ template }) {
  const points = Array.isArray(template?.points) ? template.points : [];
  return (
    <ul className="gwTemplatePoints">
      {points.map((point) => <li key={`${point.name}:${point.address}`}>{templatePointLine(point)}</li>)}
    </ul>
  );
}

// The instance fields: the three or four things that actually differ between
// two devices of the same model.
function InstanceFields({ kind, serialPorts, values, setValue }) {
  return (
    <div className="gwConnectionFields">
      <div className="gwField">
        <label className="gwLabel" htmlFor="tmpl-name">Connection name</label>
        <input id="tmpl-name" className="gwInput" value={values.name} placeholder="solis-s6-b"
          onChange={(event) => setValue("name", event.target.value)} />
      </div>
      {kind === "modbus-rtu" ? (
        <div className="gwField">
          <label className="gwLabel" htmlFor="tmpl-serial">Serial port</label>
          {serialPorts.length ? (
            <Select value={values.serialPort} options={serialPorts.map((path) => ({ value: path, label: path }))}
              onChange={(serialPort) => setValue("serialPort", serialPort)} />
          ) : (
            <input id="tmpl-serial" className="gwInput" value={values.serialPort} placeholder="/dev/serial/by-path/…"
              onChange={(event) => setValue("serialPort", event.target.value)} />
          )}
        </div>
      ) : (
        <>
          <div className="gwField">
            <label className="gwLabel" htmlFor="tmpl-host">Device host</label>
            <input id="tmpl-host" className="gwInput" value={values.host} placeholder="192.168.0.51"
              onChange={(event) => setValue("host", event.target.value)} />
          </div>
          <div className="gwField">
            <label className="gwLabel">TCP port</label>
            <NudgeNumber value={values.port} onChange={(port) => setValue("port", port)} min={1} max={65535} aria-label="TCP port" />
          </div>
        </>
      )}
      <div className="gwField">
        <label className="gwLabel">Device address</label>
        <NudgeNumber value={values.unitId} onChange={(unitId) => setValue("unitId", unitId)}
          min={kind === "modbus-rtu" ? 1 : 0} max={kind === "modbus-rtu" ? 247 : 255} aria-label="Device address" />
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="tmpl-folder">Tag folder</label>
        <input id="tmpl-folder" className="gwInput" value={values.tagFolder} placeholder="solis"
          onChange={(event) => setValue("tagFolder", event.target.value)} />
        <small className="gwHint">
          This device's tags are created under default/{values.tagFolder || "…"}/. Give each device its own folder.
        </small>
      </div>
    </div>
  );
}

/**
 * @param {Object}   props
 * @param {Object}   props.gateway
 * @param {boolean}  props.canConfigure
 * @param {string[]} props.serialPorts
 * @param {Function} [props.onUse]   receives {spec, kind, draft, tags} for the wizard; omit for browse-only
 * @param {Function} props.onClose
 */
export default function DeviceTemplatesModal({ gateway, canConfigure, serialPorts = [], onUse, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [selectedID, setSelectedID] = useState("");
  const [openPoints, setOpenPoints] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState("");
  const [instance, setInstance] = useState({ name: "", host: "", port: "502", serialPort: "", unitId: "1", tagFolder: "" });

  const load = useCallback(() => listDeviceTemplates()
    .then((rows) => { setTemplates(Array.isArray(rows) ? rows : []); setError(""); })
    .catch(() => setError("Device templates could not be loaded. Refresh the page and try again.")), []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => (templates || []).find((template) => template.id === selectedID) || null,
    [templates, selectedID],
  );

  // Choosing a template proposes its defaults; the operator still names the
  // device and gives it an address.
  const choose = (template) => {
    setSelectedID(template.id);
    setError("");
    setInstance({
      name: "",
      host: "",
      port: String(template?.settings?.default_port || 502),
      serialPort: serialPorts[0] || "",
      unitId: "1",
      tagFolder: String(template.tag_folder_default || ""),
    });
  };

  const use = async () => {
    if (!selected) return;
    setBusy("use");
    setError("");
    try {
      const kind = selected.kind;
      const result = await instantiateDeviceTemplate(selected.id, {
        gateway_id: gateway.id,
        name: instance.name.trim(),
        ...(kind === "modbus-rtu"
          ? { serial_port: instance.serialPort.trim() }
          : { host: instance.host.trim(), port: Number(instance.port) || 502 }),
        unit_id: Number(instance.unitId) || 0,
        ...(instance.tagFolder.trim() ? { tag_folder: instance.tagFolder.trim() } : {}),
      });
      const spec = result?.spec;
      if (!spec) throw new Error("The template did not produce a connection.");
      onUse?.({
        spec,
        tags: Array.isArray(result?.tags) ? result.tags : [],
        kind: edgeModbusSpecKind(spec) || kind,
        draft: edgeModbusSpecToDraft(spec),
      });
    } catch (useError) {
      setError(publicTemplateError(useError, "This device could not be prepared from the template. Check the settings and try again."));
    } finally {
      setBusy("");
    }
  };

  const clone = async (template) => {
    setBusy(`clone:${template.id}`);
    try {
      await cloneDeviceTemplate(template.id, `${template.name} copy`);
      await load();
    } catch (cloneError) {
      setError(publicTemplateError(cloneError, "The template could not be copied. Try again."));
    } finally {
      setBusy("");
    }
  };

  const remove = async (template) => {
    setBusy(`delete:${template.id}`);
    try {
      await deleteDeviceTemplate(template.id);
      setConfirmingDelete("");
      if (selectedID === template.id) setSelectedID("");
      await load();
    } catch (deleteError) {
      setError(publicTemplateError(deleteError, "The template could not be removed. Try again."));
    } finally {
      setBusy("");
    }
  };

  const picking = typeof onUse === "function";
  const ready = Boolean(selected) && instance.name.trim() &&
    (selected?.kind === "modbus-rtu" ? instance.serialPort.trim() : instance.host.trim());

  return (
    <div className="gwOverlay" style={{ zIndex: 120 }}>
      <div className="gwModal gwEdgeProgramsModal" role="dialog" aria-modal="true" aria-labelledby="device-templates-title">
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle" id="device-templates-title">Device templates</div>
            <div className="gwModalSubtitle">
              {picking
                ? `Start a connection on ${gateway.name} from a saved device, then review it as usual.`
                : "Saved register maps you can reuse on any device of the same model."}
            </div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} disabled={Boolean(busy)} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>
        <div className="gwModalBody gwEdgeProgramsBody">
          {error ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Device templates</div>
              <div className="gwAlertBody">{error}</div>
            </div>
          ) : null}
          {templates == null ? <div className="gwHint">Reading device templates…</div> : null}
          {templates && templates.length === 0 ? (
            <div className="gwNotice isSoft">
              No device templates yet. Save one from a connection that already works, and the next device of that
              model is a host and an address away.
            </div>
          ) : null}
          {(templates || []).map((template) => (
            <div className="gwEdgeProgramRow" key={template.id}>
              <div className="gwEdgeProgramMain">
                <div className="gwEdgeRuntimeTitleRow">
                  <span className="gwListTitle">{template.name}</span>
                  {template.source === "builtin" ? <span className="gwStatusPill isNeutral">Built in</span> : null}
                  {selectedID === template.id ? <span className="gwStatusPill isPositive">Selected</span> : null}
                </div>
                <div className="gwListMeta">{templateSummaryLine(template)}</div>
                {template.vendor || template.model ? (
                  <div className="gwListMeta">{[template.vendor, template.model].filter(Boolean).join(" · ")}</div>
                ) : null}
                {template.description ? <div className="gwListMeta">{template.description}</div> : null}
                {openPoints === template.id ? <TemplatePoints template={template} /> : null}
              </div>
              <div className="gwRowActions">
                {confirmingDelete === template.id ? (
                  <>
                    <span className="gwListMeta">Remove this template?</span>
                    <button type="button" className="gwGhostBtn" disabled={Boolean(busy)} onClick={() => setConfirmingDelete("")}>Cancel</button>
                    <button type="button" className="gwDangerBtn" disabled={Boolean(busy)} onClick={() => remove(template)}>
                      {busy === `delete:${template.id}` ? "Removing…" : "Remove"}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="gwRowAction" onClick={() => setOpenPoints(openPoints === template.id ? "" : template.id)}>
                      {openPoints === template.id ? "Hide points" : "View points"}
                    </button>
                    {picking ? (
                      <button type="button" className="gwRowAction" disabled={Boolean(busy)} onClick={() => choose(template)}>
                        {selectedID === template.id ? "Selected" : "Select"}
                      </button>
                    ) : null}
                    <button type="button" className="gwRowAction" disabled={!canConfigure || Boolean(busy)} onClick={() => clone(template)}>
                      {busy === `clone:${template.id}` ? "Copying…" : "Duplicate"}
                    </button>
                    {template.source === "builtin" ? (
                      // A builtin would be re-seeded on the next boot, so
                      // offering Remove would be offering a lie.
                      <span className="gwListMeta">Duplicate it to change it</span>
                    ) : (
                      <button type="button" className="gwRowAction" disabled={!canConfigure || Boolean(busy)} onClick={() => setConfirmingDelete(template.id)}>
                        Remove
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {picking && selected ? (
            <div className="gwCardSection">
              <div className="gwSectionTitle">This device</div>
              <div className="gwCardSectionBody">
                <div className="gwHint">
                  {selected.name} brings {(selected.points || []).length} points. Give this one its address; everything
                  else comes from the template.
                </div>
                <InstanceFields
                  kind={selected.kind}
                  serialPorts={serialPorts}
                  values={instance}
                  setValue={(key, value) => setInstance((current) => ({ ...current, [key]: value }))}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="gwGhostBtn" disabled={Boolean(busy)} onClick={onClose}>
            {picking ? "Cancel" : "Done"}
          </button>
          {picking ? (
            <button type="button" className="gwPrimaryBtn" disabled={!canConfigure || !ready || Boolean(busy)} onClick={use}>
              {busy === "use" ? "Preparing…" : "Fill the connection form"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * SaveAsTemplateModal turns a working connection into a reusable one. The
 * payload is built by the caller (edgeModbusTemplateFromSpec) so the stripping
 * of host, port and unit id happens in one tested place; this only names it.
 */
export function SaveAsTemplateModal({ buildPayload, defaultName, onSaved, onClose }) {
  const [name, setName] = useState(defaultName || "");
  const [vendor, setVendor] = useState("");
  const [model, setModel] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = buildPayload({ name: name.trim(), vendor: vendor.trim(), model: model.trim() });
      const saved = await createDeviceTemplate(payload);
      onSaved?.(saved);
    } catch (saveError) {
      setError(publicTemplateError(saveError, String(saveError?.message || "The template could not be saved. Try again.")));
      setBusy(false);
    }
  };

  return (
    <div className="gwOverlay" style={{ zIndex: 130 }}>
      <div className="gwModal" role="dialog" aria-modal="true" aria-labelledby="save-template-title">
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle" id="save-template-title">Save as device template</div>
            <div className="gwModalSubtitle">
              Keeps this device's register map. The host, port and device address are left out — they are what
              differs between two devices of the same model.
            </div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} disabled={busy} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>
        <div className="gwModalBody">
          {error ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Template not saved</div>
              <div className="gwAlertBody">{error}</div>
            </div>
          ) : null}
          <div className="gwConnectionFields">
            <div className="gwField">
              <label className="gwLabel" htmlFor="save-template-name">Template name</label>
              <input id="save-template-name" className="gwInput" value={name} onChange={(event) => setName(event.target.value)} placeholder="Solis S6-EH1P" />
            </div>
            <div className="gwField">
              <label className="gwLabel" htmlFor="save-template-vendor">Vendor</label>
              <input id="save-template-vendor" className="gwInput" value={vendor} onChange={(event) => setVendor(event.target.value)} placeholder="Solis" />
            </div>
            <div className="gwField">
              <label className="gwLabel" htmlFor="save-template-model">Model</label>
              <input id="save-template-model" className="gwInput" value={model} onChange={(event) => setModel(event.target.value)} placeholder="S6-EH1P" />
            </div>
          </div>
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="gwGhostBtn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="gwPrimaryBtn" disabled={busy || !name.trim()} onClick={save}>
            {busy ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}
